import { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';

import { api } from '../api/api';
import { authService } from '../auth/auth-service';
import GAuthModal from '../components/GAuthModal';

const Context = createContext();

export const ContextProvider = ({ children }) => {
	const navigate = useNavigate();

	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [userData, setUserData] = useState(null);
	const [authError, setAuthError] = useState(null);

	/* ----------------------------------
	   Global auth error listener (401/403)
	---------------------------------- */
	useEffect(() => {
		const listener = (event) => {
			setAuthError(event.detail);
			setIsAuthenticated(false);
		};

		window.addEventListener('auth-error', listener);
		return () => window.removeEventListener('auth-error', listener);
	}, []);

	/* ----------------------------------
	   Initial SSO authentication
	---------------------------------- */
	useEffect(() => {
		let active = true;
		const authenticateUser = async () => {
			try {
			await authService.initializeAuthentication(navigate, (data) => {
				if (!active) return;
				setUserData(data);
				setShowModal(true); // 2FA modal
			});

			if (active) setIsAuthenticated(authService.isAuthenticated());
			} catch (error) {
				console.error('Session initialization failed', error);
				if (active) setIsAuthenticated(false);
			} finally {
				if (active) setLoading(false);
			}
		};

		authenticateUser();
		return () => { active = false; };
	}, [navigate]);

	/* ----------------------------------
	   2FA success
	---------------------------------- */
	const handleSuccess = () => {
		setShowModal(false);
        authService.markAuthenticated();
		setIsAuthenticated(true);
		setLoading(false);

		// A completed PIN challenge must not reset a valid deep link or menu route.
		const path = window.location.pathname.replace(/\/+$/, '') || '/';
		if (path === '/' || path === '/UamUI') {
			navigate(userData?.isAdmin === 'Y'
				? '/admin/dashboard'
				: '/new-user/create', { replace: true });
		}
	};

	/* ----------------------------------
	   2FA failure
	---------------------------------- */
	const handleFailure = () => {
		setAuthError({
			status: 401,
			message: 'Authentication failed. Please contact support.',
		});
	};

	/* ----------------------------------
	   Reference data loaders
	---------------------------------- */
	const [appAccess, setAppAccess] = useState({ loading: true, data: null });
	const [financeRoles, setFinanceRoles] = useState({ loading: true, data: null });
	const [SSPFacilities, setSSPFacilities] = useState({ loading: true, data: null });

	useEffect(() => {
		if (!isAuthenticated) return;

		api.get({ path: '/getAppAccessList', service: 'useronboarding' })
			.then((res) => setAppAccess({ loading: false, data: res.data }))
			.catch(() => setAppAccess({ loading: false, data: null }));

		api.get({ path: '/getFinanceRoles', service: 'useronboarding' })
			.then((res) => setFinanceRoles({ loading: false, data: res.data }))
			.catch(() => setFinanceRoles({ loading: false, data: null }));

		api.get({ path: '/getSSPFacilities', service: 'useronboarding' })
			.then((res) => setSSPFacilities({ loading: false, data: res.data }))
			.catch(() => setSSPFacilities({ loading: false, data: null }));
	}, [isAuthenticated]);

	return (
		<Context.Provider
			value={{
				appAccess,
				financeRoles,
				SSPFacilities,
				isAuthenticated,
				loading,
			}}
		>
			{children}

			{/* 2FA Modal */}
			<GAuthModal
				visible={showModal}
				onClose={handleFailure}
				onSuccess={handleSuccess}
				userData={userData}
			/>

			{/* 401 / 403 Modal */}
			{authError && (
				<Modal
					open
					centered
					closable={false}
					maskClosable={false}
					okText="Close"
					cancelButtonProps={{ style: { display: 'none' } }}
					okButtonProps={{
						type: 'primary',
						style: { background: '#EB3680', borderColor: '#EB3680' },
					}}
					title={
						<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<LogoutOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
							<span>
								{authError.status === 403
									? 'Access Denied'
									: 'Session Expired'}
							</span>
						</div>
					}
					onOk={() => {
						window.location.assign('/');
					}}
				>
					<p style={{ margin: 0 }}>
						{authError.message ||
							'Your session has expired. This window will close.'}
					</p>
				</Modal>
			)}
		</Context.Provider>
	);
};

export default Context;
